// mc-engine.js — Multiple Choice testing engine (Brief 058).
//
// PORT TARGET: iEnglish/Classes/KFEModules/Learning/TextBasedMCQuestions/
//              KFEMultipleChoiceViewModel.{h,m} (Obj-C). Unity didn't port MC.
//
// Pure state machine — no DOM, no fetch, no audio (architecture rule #1). The
// screen (kfe-multiple-choice-exercise.js) reads snapshot() after every
// transition and feeds the learner's pick back via answer(choiceIndex).
//
// Behaviour mirrors iOS exactly (CLAUDE.md Rule #9):
//   • Questions are presented in plist order — NO shuffling (the corpus order
//     is the contract; iOS never re-sorts). gotcha §"Cycling order".
//   • Tapping a choice records it and advances immediately — iOS has NO
//     per-answer correct/wrong reveal (KFEMultipleChoiceCell only does a
//     momentary touch highlight; recordAnswerWithIndex → playNextTest fires
//     with no feedback step). So answer() both records AND advances.
//   • Score = correctCount / totalQuestions * 100 (integer percentage in the
//     snapshot's `score`). Pass = score >= passScore. No partial credit, no
//     per-question weighting. Matches KFEMultipleChoiceViewModel
//     hadRightCount / totalTest * 100 vs percentHadToPass.
//   • Sub-types (mcType 1/2/3 — Third Ear / Method / Metaphor) share ONE
//     engine and ONE rendering. iOS uses a single KFEMultipleChoiceQuesVC for
//     all three; the rendering does not diverge. Sub-type is metadata only.
//
// The constructor takes a Brief 057 test descriptor (the shape returned by
// getMcTest()): { questions, passScore, ... }. correctIndex is the 0-based
// index of the correct choice (each choice also carries a redundant `correct`
// boolean — this engine trusts correctIndex).
//
// Public API:
//   new MCEngine(testDescriptor, options?)
//   start()              → returns first MCState (mode "question" | "complete")
//   answer(choiceIndex)  → records pick, advances; returns next MCState
//   snapshot()           → current MCState
//
// Events (CustomEvent on the engine):
//   "stateChanged" — fires whenever state advances; detail = snapshot
//   "completed"    — fires once when state.mode === "complete"

/** @typedef {import("../data/mc-loader.js").McTest} McTest */
/** @typedef {import("../data/mc-loader.js").McQuestion} McQuestion */
/** @typedef {import("../data/mc-loader.js").McChoice} McChoice */

/** @typedef {"question"|"complete"} MCMode */

/**
 * @typedef {object} MCState
 * @property {MCMode}            mode
 * @property {McQuestion|null}   currentQuestion    null in "complete"
 * @property {McChoice[]}        currentChoices     [] in "complete"
 * @property {number}            questionIndex      0-based cursor
 * @property {number}            totalQuestions
 * @property {number}            correctCount       raw correct tally so far
 * @property {number}            score              correctCount / total * 100
 * @property {number}            passScore          percentage required to pass
 * @property {boolean}           pass               score >= passScore (complete only)
 */

/** Pass threshold used only when the descriptor carries no passScore. The
 *  2022 corpus always supplies one (item 111109 = 50); this is a floor for
 *  malformed/legacy data. */
const DEFAULT_PASS_SCORE = 50;

export class MCEngine extends EventTarget {
  /**
   * @param {McTest} test  descriptor from getMcTest()
   * @param {{ passScore?: number }} [options]  overrides the descriptor's threshold
   */
  constructor(test, options = {}) {
    super();
    /** @type {McQuestion[]} */
    this._questions = Array.isArray(test?.questions) ? test.questions.slice() : [];

    const fromOpts = Number(options.passScore);
    const fromTest = Number(test?.passScore);
    this._passScore = Number.isFinite(fromOpts)
      ? fromOpts
      : Number.isFinite(fromTest)
        ? fromTest
        : DEFAULT_PASS_SCORE;

    this._index = 0;
    this._correctCount = 0;
    /** @type {MCMode} */
    this._mode = "question";
    /** @type {MCState|null} */
    this._lastState = null;
  }

  // ─── Public API ───────────────────────────────────────────────

  /** @returns {MCState} */
  start() {
    this._index = 0;
    this._correctCount = 0;
    this._mode = this._questions.length > 0 ? "question" : "complete";
    return this._publish(this._buildState());
  }

  /**
   * Record the learner's pick for the current question and advance. Returns the
   * next state ("question" for the next item, or "complete" after the last).
   * Ignored once the test is complete.
   * @param {number} choiceIndex 0-based index into the current question's choices
   * @returns {MCState}
   */
  answer(choiceIndex) {
    if (this._mode !== "question") return this._publish(this._buildState());
    const q = this._questions[this._index];
    if (!q) return this._publish(this._buildState());

    if (choiceIndex === q.correctIndex) this._correctCount++;

    this._index++;
    if (this._index >= this._questions.length) {
      this._mode = "complete";
    }
    return this._publish(this._buildState());
  }

  /** @returns {MCState} */
  snapshot() {
    return this._lastState ?? this._buildState();
  }

  // ─── Internals ────────────────────────────────────────────────

  /** @returns {number} 0–100 share of correct answers over total questions. */
  _scorePercent() {
    const total = this._questions.length;
    return total > 0 ? (this._correctCount / total) * 100 : 0;
  }

  /** @returns {MCState} */
  _buildState() {
    const total = this._questions.length;
    const score = this._scorePercent();
    const complete = this._mode === "complete";
    const q = complete ? null : (this._questions[this._index] ?? null);
    return {
      mode: this._mode,
      currentQuestion: q,
      currentChoices: q ? q.choices.slice() : [],
      questionIndex: this._index,
      totalQuestions: total,
      correctCount: this._correctCount,
      score,
      passScore: this._passScore,
      pass: complete && score >= this._passScore,
    };
  }

  /**
   * @param {MCState} state
   * @returns {MCState}
   */
  _publish(state) {
    this._lastState = state;
    this.dispatchEvent(new CustomEvent("stateChanged", { detail: state }));
    if (state.mode === "complete") this.dispatchEvent(new Event("completed"));
    return state;
  }
}
